import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  effect,
  inject,
  input,
  output,
  viewChild,
} from '@angular/core';
import {
  ActiveElement,
  Chart,
  ChartConfiguration,
  ChartEvent,
  registerables,
  TooltipItem,
} from 'chart.js';
import { IntelligenceModels } from '@models/intelligence.model';
import {
  observeThemeChanges,
  readChartTheme,
  seriesColor,
  seriesFillColor,
} from '../chart-result-theme.util';
import { formatXLabel } from '../chart-result.util';
import Timeframe = IntelligenceModels.Charts.Timeframe;
import XAxisType = IntelligenceModels.Charts.XAxisType;

const TICK_FONT_SIZE = 11;
const LEGEND_BOX_WIDTH = 10;
const LEGEND_PADDING = 10;
const LEGEND_MAX_HEIGHT = 200;
const TOOLTIP_PADDING = 10;
const TOOLTIP_CORNER_RADIUS = 6;
const BAR_BORDER_RADIUS = 3;
const BAR_BORDER_WIDTH = 1;

/**
 * The chart itself: a Chart.js bar chart over one `Charts.Data` payload, themed
 * from CSS variables and re-themed on a theme switch. Actionable nonzero bars
 * emit their typed indices; inline and expanded views share the same canvas.
 */
@Component({
  selector: 'app-chart-result-canvas',
  standalone: true,
  templateUrl: './chart-result-canvas.component.html',
  styleUrl: './chart-result-canvas.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChartResultCanvasComponent implements AfterViewInit {
  public readonly data = input.required<IntelligenceModels.Charts.Data>();
  public readonly xAxisType = input<XAxisType | undefined>(undefined);
  public readonly timeframe = input<Timeframe | undefined>(undefined);
  public readonly timezone = input<string | undefined>(undefined);
  public readonly interactionHint = input<string>('');
  public readonly isSelectionActionable =
    input<IntelligenceModels.Charts.BarSelectionPredicate>(() => true);
  public readonly barSelected = output<IntelligenceModels.Charts.BarSelection>();

  private readonly canvasRef =
    viewChild.required<ElementRef<HTMLCanvasElement>>('canvas');

  private readonly destroyRef = inject(DestroyRef);

  private chart: Chart | null = null;
  private isViewReady = false;

  public constructor() {
    Chart.register(...registerables);

    // Re-render on any input change; the first run is a no-op until the
    // canvas exists, and `ngAfterViewInit` performs the initial render.
    effect(() => {
      this.data();
      this.xAxisType();
      this.timeframe();
      this.timezone();
      this.interactionHint();
      this.isSelectionActionable();
      if (this.isViewReady) this.render();
    });

    const themeObserver = observeThemeChanges(() => this.render());
    this.destroyRef.onDestroy(() => {
      themeObserver.disconnect();
      this.chart?.destroy();
      this.chart = null;
    });
  }

  public ngAfterViewInit(): void {
    this.isViewReady = true;
    this.render();
  }

  private render(): void {
    const config = this.buildConfig();
    if (this.chart) {
      this.chart.data = config.data;
      this.chart.options = config.options ?? {};
      this.chart.update();
      return;
    }
    this.chart = new Chart(this.canvasRef().nativeElement, config);
  }

  private buildConfig(): ChartConfiguration<'bar'> {
    const { labels, series } = this.data();
    const theme = readChartTheme();

    return {
      type: 'bar',
      data: {
        labels: labels.map((label) =>
          formatXLabel(label, this.xAxisType(), this.timeframe(), this.timezone()),
        ),
        datasets: series.map((item, index) => ({
          label: item.label,
          data: item.values,
          backgroundColor: seriesFillColor(theme, index),
          borderColor: seriesColor(theme, index),
          borderWidth: BAR_BORDER_WIDTH,
          borderRadius: BAR_BORDER_RADIUS,
        })),
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        interaction: { mode: 'nearest', intersect: true },
        onClick: (_event: ChartEvent, elements: ActiveElement[]) => {
          const selection = this.actionableSelection(elements[0]);
          if (selection) this.barSelected.emit(selection);
        },
        onHover: (event: ChartEvent, elements: ActiveElement[]) => {
          const target = event.native?.target;
          if (target instanceof HTMLElement) {
            target.style.cursor = this.actionableSelection(elements[0])
              ? 'pointer'
              : 'default';
          }
        },
        plugins: {
          legend: {
            display: series.length > 1,
            position: 'top',
            maxHeight: LEGEND_MAX_HEIGHT,
            labels: {
              color: theme.text,
              font: { size: TICK_FONT_SIZE },
              boxWidth: LEGEND_BOX_WIDTH,
              padding: LEGEND_PADDING,
            },
          },
          tooltip: {
            backgroundColor: theme.tooltipBackground,
            titleColor: theme.tooltipText,
            bodyColor: theme.tooltipText,
            borderColor: theme.tooltipBorder,
            borderWidth: 1,
            padding: TOOLTIP_PADDING,
            cornerRadius: TOOLTIP_CORNER_RADIUS,
            callbacks: {
              footer: (items: TooltipItem<'bar'>[]) => {
                const item = items[0];
                if (!item) return '';
                const selection = this.actionableSelection({
                  datasetIndex: item.datasetIndex,
                  index: item.dataIndex,
                });
                return selection ? this.interactionHint() : '';
              },
            },
          },
        },
        scales: {
          x: {
            grid: { display: false },
            border: { color: theme.grid },
            ticks: { color: theme.text, font: { size: TICK_FONT_SIZE } },
          },
          y: {
            beginAtZero: true,
            grid: { color: theme.grid },
            border: { display: false },
            ticks: {
              color: theme.text,
              font: { size: TICK_FONT_SIZE },
              precision: 0,
            },
          },
        },
      },
    };
  }

  private actionableSelection(
    active: Pick<ActiveElement, 'datasetIndex' | 'index'> | undefined,
  ): IntelligenceModels.Charts.BarSelection | null {
    if (!active) return null;
    const selection: IntelligenceModels.Charts.BarSelection = {
      labelIndex: active.index,
      seriesIndex: active.datasetIndex,
    };
    const value = this.data().series[selection.seriesIndex]?.values[selection.labelIndex];
    return value != null && value > 0 && this.isSelectionActionable()(selection)
      ? selection
      : null;
  }
}
